import React, { useEffect, useState, useCallback, useRef } from 'react'
import './Home.scss'
import { getDprImageUrl } from '../utils/imageLoader'
const Home = ({ images = [] }) => {
    const [hoveredItem, setHoveredItem] = useState(null)
    const [columnAssignments, setColumnAssignments] = useState([]) // [{ col: 0|1|2, image, width, span }]
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
    const [firstImageVisible, setFirstImageVisible] = useState(false)
    const [imagesPreloaded, setImagesPreloaded] = useState(false)
    const staggerTimeoutsRef = useRef([])
    const preloadedImagesRef = useRef(new Set())

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Preload all images when component mounts
    useEffect(() => {
        if (images.length === 0) return

        const preloadAllImages = async () => {
            const imageUrls = images.map((img) => getDprImageUrl(img.asset || img.src, 750))
            
            const preloadPromises = imageUrls.map((src) => {
                return new Promise((resolve) => {
                    const img = new Image()
                    img.onload = () => {
                        preloadedImagesRef.current.add(src)
                        resolve()
                    }
                    img.onerror = () => resolve() // Still resolve on error
                    img.src = src
                })
            })

            await Promise.all(preloadPromises)
            setImagesPreloaded(true)
        }

        preloadAllImages()
    }, [images])

    const getRandomColumns = useCallback((count, mobile) => {
        const cols = mobile ? [0, 1] : [0, 1, 2]
        const picked = []
        const max = Math.min(count, cols.length)
        while (picked.length < max) {
            const idx = Math.floor(Math.random() * cols.length)
            const col = cols.splice(idx, 1)[0]
            picked.push(col)
        }
        return picked
    }, [])



    const getOrientation = (img) => {
        const w = img?.asset?.fields?.file?.details?.image?.width
        const h = img?.asset?.fields?.file?.details?.image?.height
        if (!w || !h) return null
        return h > w ? 'portrait' : 'landscape'
    }

    const pickAlternatingForColumns = useCallback((cols) => {
        const sortedCols = [...cols].sort((a, b) => a - b)
        const portraits = images.filter((i) => getOrientation(i) === 'portrait')
        const landscapes = images.filter((i) => getOrientation(i) === 'landscape')

        const makeSeq = (len, start) => Array.from({ length: len }, (_, idx) => (idx % 2 === 0 ? start : (start === 'portrait' ? 'landscape' : 'portrait')))
        const feasible = (seq) => {
            const needP = seq.filter((s) => s === 'portrait').length
            const needL = seq.length - needP
            return needP <= portraits.length && needL <= landscapes.length
        }

        let seqLen = sortedCols.length
        let startType = portraits.length >= landscapes.length ? 'portrait' : 'landscape'
        let seq = makeSeq(seqLen, startType)
        if (!feasible(seq)) {
            startType = startType === 'portrait' ? 'landscape' : 'portrait'
            seq = makeSeq(seqLen, startType)
        }
        while (!feasible(seq) && seqLen > 1) {
            seqLen -= 1
            seq = makeSeq(seqLen, startType)
        }

        const result = []
        const usedPaths = new Set()
        const pickFrom = (arr) => {
            if (!arr.length) return null
            const available = arr.filter((a) => !usedPaths.has(a.path))
            if (!available.length) return null
            const idx = Math.floor(Math.random() * available.length)
            const item = available[idx]
            usedPaths.add(item.path)
            return item
        }

        for (let i = 0; i < seqLen; i++) {
            const need = seq[i]
            const img = need === 'portrait' ? pickFrom(portraits) : pickFrom(landscapes)
            if (img) {
                result.push({ col: sortedCols[i], image: img })
            }
        }

        return result
    }, [images])

    const assignRandom = useCallback((count, delay = 0) => {
        const mobile = isMobile
        const cols = getRandomColumns(count, mobile)
        const alternating = pickAlternatingForColumns(cols)

        const byCol = new Map(alternating.map((x) => [x.col, x.image]))
        const assignments = cols.map((c) => {
            // On mobile, sometimes span across both columns
            const shouldSpan = mobile && Math.random() > 0.6
            return {
                col: c,
                image: byCol.get(c),
                width: mobile ? 100 : Math.floor(Math.random() * 51) + 50, // Full width on mobile, random on desktop
                span: shouldSpan
            }
        })

        // clear any prior stagger timers
        staggerTimeoutsRef.current.forEach((t) => clearTimeout(t))
        staggerTimeoutsRef.current = []

        if (delay > 0 && assignments.length > 1) {
            // reveal assignments one-by-one with `delay` ms gap
            for (let i = 0; i < assignments.length; i++) {
                const t = setTimeout(() => {
                    setColumnAssignments(assignments.slice(0, i + 1))
                }, i * delay)
                staggerTimeoutsRef.current.push(t)
            }
        } else {
            setColumnAssignments(assignments)
        }
    }, [isMobile, getRandomColumns, pickAlternatingForColumns])

    const assignToColumns = useCallback((cols) => {
        const mobile = isMobile
        const alternating = pickAlternatingForColumns(cols)

        const byCol = new Map(alternating.map((x) => [x.col, x.image]))
        const assignments = cols.map((c) => {
            const shouldSpan = mobile && Math.random() > 0.6
            return {
                col: c,
                image: byCol.get(c),
                width: mobile ? 100 : Math.floor(Math.random() * 51) + 50,
                span: shouldSpan
            }
        })

        setColumnAssignments(assignments)
    }, [isMobile, pickAlternatingForColumns])

    useEffect(() => {
        // Initial: display 1 image on the right column only
        if (images && images.length > 0) {
            const rightCol = isMobile ? 1 : 2
            assignToColumns([rightCol])
            setFirstImageVisible(false)
            const timer = setTimeout(() => setFirstImageVisible(true), 250)
            return () => clearTimeout(timer)
        }
    }, [images, isMobile, assignToColumns])

    const handleClick = () => {
        // Show 2 or 3 random images in random columns (or 1-2 on mobile)
        const count = isMobile ? (Math.random() > 0.5 ? 2 : 1) : (Math.random() > 0.5 ? 3 : 2)
        assignRandom(Math.min(count, images.length), 50)
    }

    // Shuffle images on mouse wheel scroll
    useEffect(() => {
        const handleWheel = () => {
            const count = isMobile ? (Math.random() > 0.5 ? 2 : 1) : (Math.random() > 0.5 ? 3 : 2)
            assignRandom(Math.min(count, images.length))
        }
        window.addEventListener('wheel', handleWheel)
        return () => window.removeEventListener('wheel', handleWheel)
    }, [isMobile, images, assignRandom])

    const renderColumnContent = (colIndex) => {
        const assignment = columnAssignments.find((a) => a.col === colIndex)
        if (!assignment || !assignment.image) return null
        const { image, width, span } = assignment
        const optimizedSrc = getDprImageUrl(image.asset || image.src, 750)
        // Only apply fade-in to the first image (first column with image)
        const isFirstImage = columnAssignments.findIndex(a => a.image) === columnAssignments.indexOf(assignment)
        return (
            <div
                className={`imageWrapper ${span ? 'span-full' : ''}`}
                style={{ width: span ? '100%' : `${width}%` }}
                onMouseEnter={() => setHoveredItem(image)}
                onMouseLeave={() => setHoveredItem(null)}
            >
                <img
                    src={optimizedSrc}
                    alt={image.caption || 'Artwork'}
                    className={isFirstImage ? (firstImageVisible ? 'fade-in' : 'fade-init') : ''}
                />
            </div>
        )
    }

    const numColumns = isMobile ? 2 : 3

    return (
        <div className='homePage' onClick={handleClick}>
            <div className='gridContainer'>
                {Array.from({ length: numColumns }).map((_, col) => (
                    <div key={col} className='gridColumn'>
                        {renderColumnContent(col)}
                    </div>
                ))}
            </div>
            <div className={`homeCaption ${hoveredItem && !isMobile ? 'visible' : ''}`}>
                {hoveredItem?.caption && (
                    hoveredItem.caption
                        .split(' — ')
                        .filter(Boolean)
                        .slice(0, -1)
                        .map((part, idx) => (
                            <span key={idx}>{part}</span>
                        ))
                )}
                {hoveredItem && (
                    <span className={`stockDot ${hoveredItem.stock ? 'inStock' : 'outStock'}`} />
                )}
            </div>
        </div>
    )
}

export default Home